{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            Copyright (c) 2019 - 2021                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.Crypto;

{$DEFINE NOPP}

interface

uses
  Classes, JS, Web, Types;

type
  TCryptoEncSignEvent = procedure(ADataAsArrayBuffer: TJSArrayBuffer) of object;
  TCryptoVerifyEvent = procedure(AValid: Boolean) of object;
  TCryptoDecryptStringEvent = procedure(AData: string) of object;
  TCryptoDecryptBinaryEvent = procedure(AData: TJSUint8Array) of object;

  TCryptoKeyUsage = (kuEncrypt, kuDecrypt, kuSign, kuVerify, kuDeriveKey, kuDeriveBits, kuWrapKey, kuUnwrapKey);
  TCryptoKeyUsages = set of TCryptoKeyUsage;

  TAESEncryptionType = (aetCBC, aetGCM);
  TAESEncryptionKeyLength = (akl128, akl192, akl256);
  TCryptoDecryptResultType = (drtString, drtBinary);
  TCryptoExportImportFormat = (efRaw, efPKCS8, efSPKI, efJSON);

  TCryptoRawExportWrapEvent = procedure(ABinary: TJSUint8Array) of object;
  TCryptoJSONExportWrapEvent = procedure(AJSON: string) of object;
  TCryptoStringExportWrapEvent = procedure(AValue: string; ABinary: TJSUint8Array) of object;
  TCryptoKeyUnwrappedEvent = procedure(AUnwrappedKey: TJSCryptoKey) of object;
  TCryptoKeyWrappedEvent = procedure(AWrappedKey: TJSArrayBuffer) of object;
  TCryptoErrorEvent = procedure(AError: string) of object;

  TCryptoBase = class(TPersistent)
  private
    FOnKeyCreated: TNotifyEvent;
    FOnKeyExportedRaw: TCryptoRawExportWrapEvent;
    FOnKeyExportedJSON: TCryptoJSONExportWrapEvent;
    FOnKeyExportedPKCS: TCryptoStringExportWrapEvent;
    FOnKeyExportedSPKI: TCryptoStringExportWrapEvent;
    FOnError: TCryptoErrorEvent;
    function GetPKCSAndSPKIExportString(AResult: JSValue): string;
    procedure SetExtractableKey(const Value: Boolean);
  protected
    FExtractableKey: Boolean;
    FUsages: TCryptoKeyUsages;
    function DoErrorCatch(Err: JSValue): JSValue;
    function DoKeyExportedJSON(AResult: JSValue): JSValue; virtual;
    function DoKeyExportedPKCS(AResult: JSValue): JSValue; virtual;
    function DoKeyExportedRaw(AResult: JSValue): JSValue; virtual;
    function DoKeyExportedSPKI(AResult: JSValue): JSValue; virtual;
    function GetUsages(AUsages: TCryptoKeyUsages): TStringDynArray;
    function GetPKCSAndSPKIImportAB(APEMString: string): TJSArrayBuffer;
    property OnKeyExportedJSON: TCryptoJSONExportWrapEvent read FOnKeyExportedJSON write FOnKeyExportedJSON;
    property OnKeyExportedPKCS8: TCryptoStringExportWrapEvent read FOnKeyExportedPKCS write FOnKeyExportedPKCS;
    property OnKeyExportedRaw: TCryptoRawExportWrapEvent read FOnKeyExportedRaw write FOnKeyExportedRaw;
    property OnKeyExportedSPKI: TCryptoStringExportWrapEvent read FOnKeyExportedSPKI write FOnKeyExportedSPKI;
  public
    property ExtractableKey: Boolean read FExtractableKey write SetExtractableKey default False;
    property Usages: TCryptoKeyUsages read FUsages write FUsages default [kuEncrypt, kuDecrypt];
    property OnKeyCreated: TNotifyEvent read FOnKeyCreated write FOnKeyCreated;
    property OnError: TCryptoErrorEvent read FOnError write FOnError;
  end;

  TEncryptionBase = class(TCryptoBase)
  private
    FOnEncrypted: TCryptoEncSignEvent;
    FOnDecryptedBinary: TCryptoDecryptBinaryEvent;
    FOnDecryptedString: TCryptoDecryptStringEvent;
    FOnKeyUnwrapped: TCryptoKeyUnwrappedEvent;
    FOnKeyWrappedEvent: TCryptoKeyWrappedEvent;
  protected
    function DoDecryptedBinary(AResult: JSValue): JSValue;
    function DoDecryptedString(AResult: JSValue): JSValue;
    function DoKeyWrapped(AResult: JSValue): JSValue; virtual;
    function DoKeyUnwrapped(AResult: JSValue): JSValue; virtual;
  public
    procedure Encrypt(APlainText: string); overload; virtual;
    procedure Encrypt(ABinary: TJSUint8Array); overload; virtual;
    procedure Decrypt(AEncryptedData: TJSArrayBuffer; AResultType: TCryptoDecryptResultType); virtual;
    function EncryptP(APlainText: string): TJSPromise; overload; virtual;
    function EncryptP(ABinary: TJSUint8Array): TJSPromise; overload; virtual;
    function DecryptP(AEncryptedData: TJSArrayBuffer; AResultType: TCryptoDecryptResultType): TJSPromise; virtual;
    property OnDecryptedBinary: TCryptoDecryptBinaryEvent read FOnDecryptedBinary write FOnDecryptedBinary;
    property OnDecryptedString: TCryptoDecryptStringEvent read FOnDecryptedString write FOnDecryptedString;
    property OnEncrypted: TCryptoEncSignEvent read FOnEncrypted write FOnEncrypted;
    property OnKeyUnwrapped: TCryptoKeyUnwrappedEvent read FOnKeyUnwrapped write FOnKeyUnwrapped;
    property OnKeyWrapped: TCryptoKeyWrappedEvent read FOnKeyWrappedEvent write FOnKeyWrappedEvent;
  end;

  TAESEncryption = class(TEncryptionBase)
  private
    FCryptoKey: TJSCryptoKey;
    FType: TAESEncryptionType;
    FKeyLength: TAESEncryptionKeyLength;
    FPrivateIV: TJSUint8Array;
    FOnKeyImported: TNotifyEvent;
    procedure SetAESType(const Value: TAESEncryptionType);
    procedure SetCryptoKey(const Value: TJSCryptoKey);
    procedure SetKeyLength(const Value: TAESEncryptionKeyLength);
  protected
    function DoEncrypted(AResult: JSValue): JSValue;
    procedure DoImport(AMode: string; AArrayBuffer: TJSArrayBuffer);
    function DoImportP(AMode: string; AArrayBuffer: TJSArrayBuffer): TJSPromise;
    function DoKeyGenerated(AKey: JSValue): JSValue;
    function DoKeyImported(AKey: JSValue): JSValue;
    function DoKeyWrapped(AResult: JSValue): JSValue; override;
    function GetIVCombinedBuffer(AResultBuffer: JSValue): TJSUint8Array;
  public
    constructor Create(AType: TAESEncryptionType); overload;
    constructor Create(AType: TAESEncryptionType; AKeyLength: TAESEncryptionKeyLength); overload;
    constructor Create(AType: TAESEncryptionType; AKeyLength: TAESEncryptionKeyLength; AExtractable: Boolean); overload;
    constructor Create(AType: TAESEncryptionType; AKeyLength: TAESEncryptionKeyLength; AExtractable: Boolean; AUsages: TCryptoKeyUsages); overload;
    procedure AfterConstruction; override;
    procedure Encrypt(APlainText: string); overload; override;
    function EncryptP(APlainText: string): TJSPromise; overload; override; //ArrayBuffer
    procedure Encrypt(ABinary: TJSUint8Array); overload; override;
    function EncryptP(ABinary: TJSUint8Array): TJSPromise; overload; override; //ArrayBuffer
    procedure ExportKey(AFormat: TCryptoExportImportFormat);
    function ExportKeyP(AFormat: TCryptoExportImportFormat): TJSPromise; //string or Uint8Array
    procedure Decrypt(AEncryptedData: TJSArrayBuffer; AResultType: TCryptoDecryptResultType); override;
    function DecryptP(AEncryptedData: TJSArrayBuffer; AResultType: TCryptoDecryptResultType): TJSPromise; override; //string or Uint8Array
    procedure GenerateKey;
    function GenerateKeyP: TJSPromise; //nothing
    procedure ImportKey(AJSON: string); overload;
    procedure ImportKey(ABinary: TJSUint8Array); overload;
    procedure ImportKey(ARaw: TJSArrayBuffer); overload;
    function ImportKeyP(AJSON: string): TJSPromise; overload;
    function ImportKeyP(ABinary: TJSUint8Array): TJSPromise; overload;
    function ImportKeyP(ARaw: TJSArrayBuffer): TJSPromise; overload;
    procedure UnwrapKey(AImportFormat: TCryptoExportImportFormat; AKey: TJSArrayBuffer; AKeyAlgorithm: JSValue; AExtractable: Boolean; AKeyUsages: TCryptoKeyUsages);
    function UnwrapKeyP(AImportFormat: TCryptoExportImportFormat; AKey: TJSArrayBuffer; AKeyAlgorithm: JSValue; AExtractable: Boolean; AKeyUsages: TCryptoKeyUsages): TJSPromise;
    procedure WrapKey(AKey: TJSCryptoKey; AExportFormat: TCryptoExportImportFormat);
    function WrapKeyP(AKey: TJSCryptoKey; AExportFormat: TCryptoExportImportFormat): TJSPromise;
    property AESType: TAESEncryptionType read FType write SetAESType default aetCBC;
    property CryptoKey: TJSCryptoKey read FCryptoKey write SetCryptoKey;
    property KeyLength: TAESEncryptionKeyLength read FKeyLength write SetKeyLength default akl128;
    property OnKeyExportedJSON: TCryptoJSONExportWrapEvent read FOnKeyExportedJSON write FOnKeyExportedJSON;
    property OnKeyExportedRaw: TCryptoRawExportWrapEvent read FOnKeyExportedRaw write FOnKeyExportedRaw;
    property OnKeyImported: TNotifyEvent read FOnKeyImported write FOnKeyImported;
  end;

  TWebAESEncryption = class(TAESEncryption);

  TRSAModulusLength = (rml2048, rml3072, rml4096);
  TCryptoHash = (ehSHA1, ehSHA256, ehSHA384, ehSHA512);
  TCryptoAsymKeyType = (aktPrivate, aktPublic);

  TRSAEncryption = class(TEncryptionBase)
  private
    FPrivateKey: TJSCryptoKey;
    FHash: TCryptoHash;
    FPublicKey: TJSCryptoKey;
    FModulusLength: TRSAModulusLength;
    FOnPublicKeyImported: TNotifyEvent;
    FOnPrivateKeyImported: TNotifyEvent;
    procedure SetPrivateKey(const Value: TJSCryptoKey);
    procedure SetPublicKey(const Value: TJSCryptoKey);
    procedure SetHash(const Value: TCryptoHash);
    procedure SetModulusLength(const Value: TRSAModulusLength);
  protected
    function DoEncrypted(AResult: JSValue): JSValue;
    procedure DoImportPrivate(AMode: string; AArrayBuffer: TJSArrayBuffer);
    procedure DoImportPublic(AMode: string; AArrayBuffer: TJSArrayBuffer);
    function DoImportPrivateP(AMode: string; AArrayBuffer: TJSArrayBuffer): TJSPromise;
    function DoImportPublicP(AMode: string; AArrayBuffer: TJSArrayBuffer): TJSPromise;
    function DoKeyGenerated(AKey: JSValue): JSValue;
    function DoPrivateKeyImported(AKey: JSValue): JSValue;
    function DoPublicKeyImported(AKey: JSValue): JSValue;
  public
    constructor Create; overload;
    constructor Create(AModulusLength: TRSAModulusLength); overload;
    constructor Create(AModulusLength: TRSAModulusLength; AHash: TCryptoHash); overload;
    constructor Create(AModulusLength: TRSAModulusLength; AHash: TCryptoHash; AExtractable: Boolean); overload;
    constructor Create(AModulusLength: TRSAModulusLength; AHash: TCryptoHash; AExtractable: Boolean; AUsages: TCryptoKeyUsages); overload;
    procedure AfterConstruction; override;
    procedure Encrypt(APlainText: string); overload; override;
    function EncryptP(APlainText: string): TJSPromise; overload; override;
    procedure Encrypt(ABinary: TJSUint8Array); overload; override;
    function EncryptP(ABinary: TJSUint8Array): TJSPromise; overload; override;
    procedure ExportKey(AKeyType: TCryptoAsymKeyType; AFormat: TCryptoExportImportFormat);
    function ExportKeyP(AKeyType: TCryptoAsymKeyType; AFormat: TCryptoExportImportFormat): TJSPromise;
    procedure Decrypt(AEncryptedData: TJSArrayBuffer; AResultType: TCryptoDecryptResultType); override;
    function DecryptP(AEncryptedData: TJSArrayBuffer; AResultType: TCryptoDecryptResultType): TJSPromise; override;
    procedure GenerateKey;
    function GenerateKeyP: TJSPromise;
    procedure ImportKey(AKey: string; AKeyType: TCryptoAsymKeyType; AFormat: TCryptoExportImportFormat); overload;
    function ImportKeyP(AKey: string; AKeyType: TCryptoAsymKeyType; AFormat: TCryptoExportImportFormat): TJSPromise; overload;
    procedure ImportKey(ABinary: TJSUint8Array; AKeyType: TCryptoAsymKeyType); overload; //PKCS#8 if private, SPKI if public
    function ImportKeyP(ABinary: TJSUint8Array; AKeyType: TCryptoAsymKeyType): TJSPromise; overload;
    procedure WrapKey(AKey: TJSCryptoKey; AExportFormat: TCryptoExportImportFormat);
    function WrapKeyP(AKey: TJSCryptoKey; AExportFormat: TCryptoExportImportFormat): TJSPromise;
    procedure UnwrapKey(AImportFormat: TCryptoExportImportFormat; AKey: TJSArrayBuffer; AKeyAlgorithm: JSValue; AExtractable: Boolean; AKeyUsages: TCryptoKeyUsages);
    function UnwrapKeyP(AImportFormat: TCryptoExportImportFormat; AKey: TJSArrayBuffer; AKeyAlgorithm: JSValue; AExtractable: Boolean; AKeyUsages: TCryptoKeyUsages): TJSPromise;
    property Hash: TCryptoHash read FHash write SetHash;
    property ModulusLength: TRSAModulusLength read FModulusLength write SetModulusLength;
    property PrivateKey: TJSCryptoKey read FPrivateKey write SetPrivateKey;
    property PublicKey: TJSCryptoKey read FPublicKey write SetPublicKey;
    property OnKeyExportedJSON: TCryptoJSONExportWrapEvent read FOnKeyExportedJSON write FOnKeyExportedJSON;
    property OnKeyExportedPKCS8: TCryptoStringExportWrapEvent read FOnKeyExportedPKCS write FOnKeyExportedPKCS;
    property OnKeyExportedSPKI: TCryptoStringExportWrapEvent read FOnKeyExportedSPKI write FOnKeyExportedSPKI;
    property OnPrivateKeyImported: TNotifyEvent read FOnPrivateKeyImported write FOnPrivateKeyImported;
    property OnPublicKeyImported: TNotifyEvent read FOnPublicKeyImported write FOnPublicKeyImported;
  end;

  TWebRSAEncryption = class(TRSAEncryption);

  TSignatureBase = class(TCryptoBase)
  private
    FOnSigned: TCryptoEncSignEvent;
    FOnVerify: TCryptoVerifyEvent;
  protected
    function DoSignatureCreated(AResult: JSValue): JSValue;
    function DoSignatureVerify(AResult: JSValue): JSValue;
  public
    procedure Sign(AText: string); overload; virtual;
    function SignP(AText: string): TJSPromise; overload; virtual;
    procedure Sign(ABinary: TJSUint8Array); overload; virtual;
    function SignP(ABinary: TJSUint8Array): TJSPromise; overload; virtual;
    procedure Verify(ASignature: TJSArrayBuffer; AData: TJSArrayBuffer); virtual;
    function VerifyP(ASignature: TJSArrayBuffer; AData: TJSArrayBuffer): TJSPromise; virtual;
    property OnSigned: TCryptoEncSignEvent read FOnSigned write FOnSigned;
    property OnVerify: TCryptoVerifyEvent read FOnVerify write FOnVerify;
  end;

  TRSASignature = class(TSignatureBase)
  private
    FPrivateKey: TJSCryptoKey;
    FHash: TCryptoHash;
    FPublicKey: TJSCryptoKey;
    FModulusLength: TRSAModulusLength;
    FOnPublicKeyImported: TNotifyEvent;
    FOnPrivateKeyImported: TNotifyEvent;
    procedure SetPrivateKey(const Value: TJSCryptoKey);
    procedure SetPublicKey(const Value: TJSCryptoKey);
  protected
    procedure DoImportPrivate(AMode: string; AArrayBuffer: TJSArrayBuffer);
    function DoImportPrivateP(AMode: string; AArrayBuffer: TJSArrayBuffer): TJSPromise;
    procedure DoImportPublic(AMode: string; AArrayBuffer: TJSArrayBuffer);
    function DoImportPublicP(AMode: string; AArrayBuffer: TJSArrayBuffer): TJSPromise;
    function DoKeyGenerated(AKey: JSValue): JSValue;
    function DoPrivateKeyImported(AKey: JSValue): JSValue;
    function DoPublicKeyImported(AKey: JSValue): JSValue;
  public
    constructor Create; overload;
    constructor Create(AModulusLength: TRSAModulusLength); overload;
    constructor Create(AModulusLength: TRSAModulusLength; AHash: TCryptoHash); overload;
    constructor Create(AModulusLength: TRSAModulusLength; AHash: TCryptoHash; AExtractable: Boolean); overload;
    constructor Create(AModulusLength: TRSAModulusLength; AHash: TCryptoHash; AExtractable: Boolean; AUsages: TCryptoKeyUsages); overload;
    procedure AfterConstruction; override;
    procedure ExportKey(AKeyType: TCryptoAsymKeyType; AFormat: TCryptoExportImportFormat);
    function ExportKeyP(AKeyType: TCryptoAsymKeyType; AFormat: TCryptoExportImportFormat): TJSPromise;
    procedure GenerateKey;
    function GenerateKeyP: TJSPromise;
    procedure ImportKey(AKey: string; AKeyType: TCryptoAsymKeyType; AFormat: TCryptoExportImportFormat); overload;
    function ImportKeyP(AKey: string; AKeyType: TCryptoAsymKeyType; AFormat: TCryptoExportImportFormat): TJSPromise; overload;
    procedure ImportKey(ABinary: TJSUint8Array; AKeyType: TCryptoAsymKeyType); overload; //PKCS#8 if private, SPKI if public
    function ImportKeyP(ABinary: TJSUint8Array; AKeyType: TCryptoAsymKeyType): TJSPromise; overload;
    procedure Sign(AText: string); overload; override;
    function SignP(AText: string): TJSPromise; overload; override;
    procedure Sign(ABinary: TJSUint8Array); overload; override;
    function SignP(ABinary: TJSUint8Array): TJSPromise; overload; override;
    procedure Verify(ASignature: TJSArrayBuffer; AData: TJSArrayBuffer); override;
    function VerifyP(ASignature: TJSArrayBuffer; AData: TJSArrayBuffer): TJSPromise; override;
    property Hash: TCryptoHash read FHash;
    property ModulusLength: TRSAModulusLength read FModulusLength;
    property PrivateKey: TJSCryptoKey read FPrivateKey write SetPrivateKey;
    property PublicKey: TJSCryptoKey read FPublicKey write SetPublicKey;
    property OnKeyExportedJSON: TCryptoJSONExportWrapEvent read FOnKeyExportedJSON write FOnKeyExportedJSON;
    property OnKeyExportedPKCS8: TCryptoStringExportWrapEvent read FOnKeyExportedPKCS write FOnKeyExportedPKCS;
    property OnKeyExportedSPKI: TCryptoStringExportWrapEvent read FOnKeyExportedSPKI write FOnKeyExportedSPKI;
    property OnPrivateKeyImported: TNotifyEvent read FOnPrivateKeyImported write FOnPrivateKeyImported;
    property OnPublicKeyImported: TNotifyEvent read FOnPublicKeyImported write FOnPublicKeyImported;
  end;

  TWebRSASignature = class(TRSASignature);

  THMACSignature = class(TSignatureBase)
  private
    FHash: TCryptoHash;
    FCryptoKey: TJSCryptoKey;
    FOnKeyImported: TNotifyEvent;
    procedure SetCryptoKey(const Value: TJSCryptoKey);
  protected
    procedure DoImport(AMode: string; AArrayBuffer: TJSArrayBuffer);
    function DoImportP(AMode: string; AArrayBuffer: TJSArrayBuffer): TJSPromise;
    function DoKeyGenerated(AKey: JSValue): JSValue;
    function DoKeyImported(AKey: JSValue): JSValue;
  public
    constructor Create; overload;
    constructor Create(AHash: TCryptoHash); overload;
    constructor Create(AHash: TCryptoHash; AExtractable: Boolean); overload;
    constructor Create(AHash: TCryptoHash; AExtractable: Boolean; AUsages: TCryptoKeyUsages); overload;
    procedure AfterConstruction; override;
    procedure GenerateKey;
    function GenerateKeyP: TJSPromise;
    procedure ExportKey(AFormat: TCryptoExportImportFormat);
    function ExportKeyP(AFormat: TCryptoExportImportFormat): TJSPromise;
    procedure ImportKey(AJSON: string); overload;
    function ImportKeyP(AJSON: string): TJSPromise; overload;
    procedure ImportKey(ABinary: TJSUint8Array); overload;
    function ImportKeyP(ABinary: TJSUint8Array): TJSPromise; overload;
    procedure ImportKey(ARaw: TJSArrayBuffer); overload;
    function ImportKeyP(ARaw: TJSArrayBuffer): TJSPromise; overload;
    procedure Sign(AText: string); overload; override;
    function SignP(AText: string): TJSPromise; overload; override;
    procedure Sign(ABinary: TJSUint8Array); overload; override;
    function SignP(ABinary: TJSUint8Array): TJSPromise; overload; override;
    procedure Verify(ASignature: TJSArrayBuffer; AData: TJSArrayBuffer); override;
    function VerifyP(ASignature: TJSArrayBuffer; AData: TJSArrayBuffer): TJSPromise; override;
    property CryptoKey: TJSCryptoKey read FCryptoKey write SetCryptoKey;
    property Hash: TCryptoHash read FHash;
    property OnKeyExportedJSON: TCryptoJSONExportWrapEvent read FOnKeyExportedJSON write FOnKeyExportedJSON;
    property OnKeyExportedRaw: TCryptoRawExportWrapEvent read FOnKeyExportedRaw write FOnKeyExportedRaw;
    property OnKeyImported: TNotifyEvent read FOnKeyImported write FOnKeyImported;
  end;

  TWebHMACSignature = class(THMACSignature);

  TSHAHash = class(TPersistent)
  private
    FHashType: TCryptoHash;
  public
    constructor Create; overload;
    constructor Create(AHashType: TCryptoHash); overload;
    function Hash(AText: string): TJSPromise;
    property HashType: TCryptoHash read FHashType write FHashType;
  end;

  TWebSHAHash = class(TSHAHash);

var
  TCryptoKeyUsageValue: array[TCryptoKeyUsage] of string = ('encrypt', 'decrypt', 'sign', 'verify', 'deriveKey',
    'deriveBits', 'wrapKey', 'unwrapKey');

const
  IVLEN = 16;

function ABToBase(AArrayBuffer: TJSArrayBuffer): string;
function ABToBinaryString(AArrayBuffer: TJSArrayBuffer): string;
function BaseToAB(ABase: string): TJSArrayBuffer;
function BinaryStringToAB(ABinaryString: string): TJSArrayBuffer;
function GetMessageEncoding(APlainText: string): TJSUint8Array;
function GetMessageStringDecoding(AArray: TJSArrayBuffer): string;
function IsTextEncoderSupported: Boolean;
function ABToHex(AArrayBuffer: TJSArrayBuffer): string;

implementation

uses
  SysUtils;

function ABToBase(AArrayBuffer: TJSArrayBuffer): string;
var
  binary: string;
begin
  binary := ABToBinaryString(AArrayBuffer);
  Result := window.btoa(binary);
end;

function ABToBinaryString(AArrayBuffer: TJSArrayBuffer): string;
var
  bytes: TJSUint8Array;
  len, I: Integer;
begin
  Result := '';
  bytes := TJSUint8Array.new(AArrayBuffer);
  len := bytes.byteLength;

  for I := 0 to len - 1 do
  begin
    Result := Result + TJSString.fromCharCode(bytes[I]);
  end;
end;

function ABToHex(AArrayBuffer: TJSArrayBuffer): string;
var
  dv: TJSDataView;
  I: Integer;
  value: LongWord;
begin
  Result := '';
  dv := TJSDataView.new(AArrayBuffer);
  for I := 0 to (dv.byteLength div 4) - 1 do
  begin
    value := dv.getUint32(I * 4);
    Result := Result + IntToHex(value, 8);
  end;
end;

function BaseToAB(ABase: string): TJSArrayBuffer;
var
  binary: string;
begin
  binary := window.atob(ABase);
  Result := BinaryStringToAB(binary);
end;

function BinaryStringToAB(ABinaryString: string): TJSArrayBuffer;
var
  len, I: Integer;
  ab: TJSArrayBuffer;
  view: TJSUint8Array;
  binary: TJSString;
begin
  binary := TJSString.New(ABinaryString);
  len := binary.length;
  ab := TJSArrayBuffer.new(len);
  view := TJSUint8Array.new(ab);
  for I := 0 to len - 1 do
  begin
    view[I] := binary.charCodeAt(I);
  end;
  Result := view.buffer;
end;

function GetMessageEncoding(APlainText: string): TJSUint8Array;
var
  arr: TJSUint8Array;
begin
  asm
    let enc = new TextEncoder();
    arr = enc.encode(APlainText);
  end;
  Result := arr;
end;

function GetMessageStringDecoding(AArray: TJSArrayBuffer): string;
var
  s: string;
begin
  asm
    let dec = new TextDecoder();
    s = dec.decode(AArray);
  end;
  Result := s;
end;

function IsTextEncoderSupported: Boolean;
var
  b: Boolean;
begin
  asm
    b = typeof(TextEncoder) != 'undefined';
  end;
  Result := b;
end;

{ TEncryptionBase }

procedure TEncryptionBase.Decrypt(AEncryptedData: TJSArrayBuffer;
  AResultType: TCryptoDecryptResultType);
begin
  //
end;

function TEncryptionBase.DecryptP(AEncryptedData: TJSArrayBuffer;
  AResultType: TCryptoDecryptResultType): TJSPromise;
begin
  Result := nil;
end;

function TEncryptionBase.DoDecryptedBinary(AResult: JSValue): JSValue;
var
  bin: TJSUint8Array;
begin
  bin := TJSUint8Array.new(TJSArrayBuffer(AResult));
  if Assigned(OnDecryptedBinary) then
    OnDecryptedBinary(bin);

  Result := bin;
end;

function TEncryptionBase.DoDecryptedString(AResult: JSValue): JSValue;
var
  str: string;
begin
  str := GetMessageStringDecoding(TJSArrayBuffer(AResult));

  if Assigned(OnDecryptedString) then
    OnDecryptedString(str);

  Result := str;
end;

function TEncryptionBase.DoKeyUnwrapped(AResult: JSValue): JSValue;
var
  c: TJSCryptoKey;
begin
  c := TJSCryptoKey(AResult);

  if Assigned(OnKeyUnwrapped) then
    OnKeyUnwrapped(c);

  Result := c;
end;

function TEncryptionBase.DoKeyWrapped(AResult: JSValue): JSValue;
begin
  if Assigned(OnKeyWrapped) then
    OnKeyWrapped(TJSArrayBuffer(AResult));

  Result := TJSArrayBuffer(AResult);
end;

procedure TEncryptionBase.Encrypt(APlainText: string);
begin
  //
end;

procedure TEncryptionBase.Encrypt(ABinary: TJSUint8Array);
begin
  //
end;

function TEncryptionBase.EncryptP(APlainText: string): TJSPromise;
begin
  Result := nil;
end;

function TEncryptionBase.EncryptP(ABinary: TJSUint8Array): TJSPromise;
begin
  Result := nil;
end;

{ TAESEncryption }

constructor TAESEncryption.Create(AType: TAESEncryptionType);
begin
  FType := AType;
  FKeyLength := akl128;
  FExtractableKey := False;
  FUsages := [kuEncrypt, kuDecrypt];
end;

constructor TAESEncryption.Create(AType: TAESEncryptionType;
  AKeyLength: TAESEncryptionKeyLength);
begin
  FType := AType;
  FKeyLength := AKeyLength;
  FExtractableKey := False;
  FUsages := [kuEncrypt, kuDecrypt]
end;

constructor TAESEncryption.Create(AType: TAESEncryptionType;
  AKeyLength: TAESEncryptionKeyLength; AExtractable: Boolean);
begin
  FType := AType;
  FKeyLength := AKeyLength;
  FExtractableKey := AExtractable;
  FUsages := [kuEncrypt, kuDecrypt];
end;

procedure TAESEncryption.AfterConstruction;
begin
  inherited;
  GenerateKey;
end;

constructor TAESEncryption.Create(AType: TAESEncryptionType;
  AKeyLength: TAESEncryptionKeyLength; AExtractable: Boolean;
  AUsages: TCryptoKeyUsages);
begin
  FType := AType;
  FKeyLength := AKeyLength;
  FExtractableKey := AExtractable;
  FUsages := AUsages;
end;

procedure TAESEncryption.Decrypt(AEncryptedData: TJSArrayBuffer; AResultType: TCryptoDecryptResultType);
begin
  DecryptP(AEncryptedData, AResultType).catch(@DoErrorCatch);
end;

function TAESEncryption.DecryptP(AEncryptedData: TJSArrayBuffer;
  AResultType: TCryptoDecryptResultType): TJSPromise;
var
  buffer, data, iv: TJSUint8Array;
  I: Integer;
  alg: TJSObject;
begin
  iv := TJSUint8Array.new(IVLEN);
  buffer := TJSUint8Array.new(AEncryptedData);
  data := TJSUint8Array.new(buffer.length - IVLEN);

  for I := 0 to buffer.length - 1 do
  begin
    if I < IVLEN then
      iv[I] := buffer[I]
    else
      data[I - IVLEN] := buffer[I];
  end;

  case FType of
    aetCBC: alg := new(['name', 'AES-CBC', 'iv', iv]);
    aetGCM: alg := new(['name', 'AES-GCM', 'iv', iv]);
  end;

  case AResultType of
    drtString: Result := window.crypto.subtle.decrypt(alg, FCryptoKey, data)._then(@DoDecryptedString);
    drtBinary: Result := window.crypto.subtle.decrypt(alg, FCryptoKey, data)._then(@DoDecryptedBinary);
  end;
end;

function TAESEncryption.DoEncrypted(AResult: JSValue): JSValue;
var
  buffer: TJSUint8Array;
begin
  buffer := GetIVCombinedBuffer(AResult);

  if Assigned(OnEncrypted) then
    OnEncrypted(buffer.buffer);

  Result := buffer.buffer;
end;

procedure TAESEncryption.DoImport(AMode: string; AArrayBuffer: TJSArrayBuffer);
begin
  DoImportP(AMode, AArrayBuffer).catch(@DoErrorCatch);
end;

function TAESEncryption.DoImportP(AMode: string;
  AArrayBuffer: TJSArrayBuffer): TJSPromise;
var
  usg: TStringDynArray;
  o: TJSObject;
begin
  usg := GetUsages(Usages);

  case FType of
    aetCBC: o := new(['name', 'AES-CBC']);
    aetGCM: o := new(['name', 'AES-GCM']);
  end;

  Result := window.crypto.subtle.importKey(AMode, TJSObject(AArrayBuffer), o, FExtractableKey, usg)._then(@DoKeyImported);
end;

function TAESEncryption.DoKeyImported(AKey: JSValue): JSValue;
begin
  FCryptoKey := TJSCryptoKey(AKey);

  if Assigned(OnKeyImported) then
    OnKeyImported(Self);

  Result := True;
end;

function TAESEncryption.DoKeyWrapped(AResult: JSValue): JSValue;
var
  buff: TJSUint8Array;
begin
  buff := GetIVCombinedBuffer(AResult);

  if Assigned(OnKeyWrapped) then
    OnKeyWrapped(buff.buffer);

  Result := buff.buffer;
end;

function TAESEncryption.DoKeyGenerated(AKey: JSValue): JSValue;
begin
  if not Assigned(FCryptoKey) then
  begin
    FCryptoKey := TJSCryptoKey(AKey);

    if Assigned(OnKeyCreated) then
      OnKeyCreated(Self);
  end;

  Result := True;
end;

procedure TAESEncryption.Encrypt(ABinary: TJSUint8Array);
begin
  EncryptP(ABinary).catch(@DoErrorCatch);
end;

function TAESEncryption.EncryptP(ABinary: TJSUint8Array): TJSPromise;
var
  alg: TJSObject;
begin
  FPrivateIV := TJSUint8Array.new(IVLEN);
  window.crypto.getRandomValues(FPrivateIV);

  case FType of
    aetCBC: alg := new(['name', 'AES-CBC', 'iv', FPrivateIV]);
    aetGCM: alg := new(['name', 'AES-GCM', 'iv', FPrivateIV]);
  end;

  Result := window.crypto.subtle.encrypt(alg, FCryptoKey, ABinary)._then(@DoEncrypted);
end;

function TAESEncryption.EncryptP(APlainText: string): TJSPromise;
var
  encoded: TJSUint8Array;
  alg: TJSObject;
begin
  if not IsTextEncoderSupported then
    Exit;

  encoded := GetMessageEncoding(APlainText);
  FPrivateIV := TJSUint8Array.new(IVLEN);
  window.crypto.getRandomValues(FPrivateIV);

  case FType of
    aetCBC: alg := new(['name', 'AES-CBC', 'iv', FPrivateIV]);
    aetGCM: alg := new(['name', 'AES-GCM', 'iv', FPrivateIV]);
  end;

  Result := window.crypto.subtle.encrypt(alg, FCryptoKey, encoded)._then(@DoEncrypted);
end;

function TAESEncryption.ExportKeyP(
  AFormat: TCryptoExportImportFormat): TJSPromise;
begin
  case AFormat of
    efRaw: Result := window.crypto.subtle.exportKey('raw', FCryptoKey)._then(@DoKeyExportedRaw);
    efJSON: Result := window.crypto.subtle.exportKey('jwk', FCryptoKey)._then(@DoKeyExportedJSON);
  end;
end;

procedure TAESEncryption.ExportKey(AFormat: TCryptoExportImportFormat);
begin
  ExportKeyP(AFormat).catch(@DoErrorCatch);
end;

procedure TAESEncryption.Encrypt(APlainText: string);
begin
  EncryptP(APlainText).catch(@DoErrorCatch);
end;

procedure TAESEncryption.GenerateKey;
begin
  GenerateKeyP.catch(@DoErrorCatch);
end;

function TAESEncryption.GenerateKeyP: TJSPromise;
var
  usg: TStringDynArray;
  alg: TJSObject;
  len: Integer;
begin
  usg := GetUsages(Usages);

  case FKeyLength of
    akl128: len := 128;
    akl192: len := 192;
    akl256: len := 256;
  end;

  case FType of
    aetCBC: alg := new(['name', 'AES-CBC', 'length', len]);
    aetGCM: alg := new(['name', 'AES-GCM', 'length', len]);
  end;

  Result := window.crypto.subtle.generateKey(alg, FExtractableKey, usg)._then(@DoKeyGenerated);
end;

function TAESEncryption.GetIVCombinedBuffer(AResultBuffer: JSValue): TJSUint8Array;
var
  view: TJSUint8Array;
  I: Integer;
begin
  view := TJSUint8Array.new(TJSArrayBuffer(AResultBuffer));
  Result := TJSUint8Array.new(FPrivateIV.length + view.length);
  for I := 0 to (FPrivateIV.length + view.length) do
  begin
    if I < IVLEN then
      Result[I] := FPrivateIV[I]
    else
      Result[I] := view[I - IVLEN];
  end;
end;

procedure TAESEncryption.ImportKey(AJSON: string);
var
  ab: TJSArrayBuffer;
begin
  ab := TJSArrayBuffer(TJSJSON.parse(AJSON));
  DoImport('jwk', ab);
end;

procedure TAESEncryption.ImportKey(ARaw: TJSArrayBuffer);
begin
  DoImport('raw', ARaw);
end;

function TAESEncryption.ImportKeyP(AJSON: string): TJSPromise;
var
  ab: TJSArrayBuffer;
begin
  ab := TJSArrayBuffer(TJSJSON.parse(AJSON));
  Result := DoImportP('jwk', ab);
end;

function TAESEncryption.ImportKeyP(ABinary: TJSUint8Array): TJSPromise;
begin
  Result := DoImportP('raw', ABinary.buffer);
end;

function TAESEncryption.ImportKeyP(ARaw: TJSArrayBuffer): TJSPromise;
begin
  Result := DoImportP('raw', ARaw);
end;

procedure TAESEncryption.ImportKey(ABinary: TJSUint8Array);
begin
  DoImport('raw', ABinary.buffer);
end;

procedure TAESEncryption.SetAESType(const Value: TAESEncryptionType);
begin
  if FType <> Value then
    FType := Value;
end;

procedure TAESEncryption.SetCryptoKey(const Value: TJSCryptoKey);
begin
  FCryptoKey := Value;
end;

procedure TAESEncryption.SetKeyLength(const Value: TAESEncryptionKeyLength);
begin
  FKeyLength := Value;
end;

procedure TAESEncryption.UnwrapKey(AImportFormat: TCryptoExportImportFormat;
  AKey: TJSArrayBuffer; AKeyAlgorithm: JSValue; AExtractable: Boolean;
  AKeyUsages: TCryptoKeyUsages);
begin
  UnwrapKeyP(AImportFormat, AKey, AKeyAlgorithm, AExtractable, AKeyUsages).catch(@DoErrorCatch);
end;

function TAESEncryption.UnwrapKeyP(AImportFormat: TCryptoExportImportFormat;
  AKey: TJSArrayBuffer; AKeyAlgorithm: JSValue; AExtractable: Boolean;
  AKeyUsages: TCryptoKeyUsages): TJSPromise;
var
  f: string;
  iv, buffer, data: TJSUint8Array;
  usg: TStringDynArray;
  I: Integer;
  alg: TJSObject;
begin
  case AImportFormat of
    efRaw: f := 'raw';
    efPKCS8: f := 'pkcs8';
    efSPKI: f := 'spki';
    efJSON: f := 'jwk';
  end;

  //separate iv from arraybuffer
  usg := GetUsages(AKeyUsages);
  iv := TJSUint8Array.new(IVLEN);
  buffer := TJSUint8Array.new(AKey);
  data := TJSUint8Array.new(buffer.length - IVLEN);

  for I := 0 to buffer.length - 1 do
  begin
    if I < IVLEN then
      iv[I] := buffer[I]
    else
      data[I - IVLEN] := buffer[I];
  end;

  //get unwrapping algorithm
  case FType of
    aetCBC: alg := new(['name', 'AES-CBC', 'iv', iv]);
    aetGCM: alg := new(['name', 'AES-GCM', 'iv', iv]);
  end;

  Result := window.crypto.subtle.unwrapkey(f, data, FCryptoKey, alg, AKeyAlgorithm, AExtractable, usg)._then(@DoKeyUnwrapped);
end;

function TAESEncryption.WrapKeyP(AKey: TJSCryptoKey;
  AExportFormat: TCryptoExportImportFormat): TJSPromise;
var
  wa: TJSObject;
  f: string;
begin
  FPrivateIV := TJSUint8Array.new(IVLEN);
  window.crypto.getRandomValues(FPrivateIV);

  case FType of
    aetCBC: wa := new(['name', 'AES-CBC', 'iv', FPrivateIV]);
    aetGCM: wa := new(['name', 'AES-GCM', 'iv', FPrivateIV]);
  end;

  case AExportFormat of
    efRaw: f := 'raw';
    efPKCS8: f := 'pkcs8';
    efSPKI: f := 'spki';
    efJSON: f := 'jwk';
  end;

  Result := window.crypto.subtle.wrapKey(f, AKey, FCryptoKey, wa)._then(@DoKeyWrapped);
end;

procedure TAESEncryption.WrapKey(AKey: TJSCryptoKey;
  AExportFormat: TCryptoExportImportFormat);
begin
  WrapKeyP(AKey, AExportFormat).catch(@DoErrorCatch);
end;

{ TRSAEncryption }

procedure TRSAEncryption.AfterConstruction;
begin
  inherited;
  GenerateKey;
end;

constructor TRSAEncryption.Create(AModulusLength: TRSAModulusLength);
begin
  FModulusLength := AModulusLength;
  FHash := ehSHA256;
  FExtractableKey := False;
  FUsages := [kuEncrypt, kuDecrypt];
end;

constructor TRSAEncryption.Create;
begin
  FModulusLength := rml4096;
  FHash := ehSHA256;
  FExtractableKey := False;
  FUsages := [kuEncrypt, kuDecrypt];
end;

constructor TRSAEncryption.Create(AModulusLength: TRSAModulusLength;
  AHash: TCryptoHash);
begin
  FModulusLength := AModulusLength;
  FHash := AHash;
  FExtractableKey := False;
  FUsages := [kuEncrypt, kuDecrypt];
end;

constructor TRSAEncryption.Create(AModulusLength: TRSAModulusLength;
  AHash: TCryptoHash; AExtractable: Boolean; AUsages: TCryptoKeyUsages);
begin
  FModulusLength := AModulusLength;
  FHash := AHash;
  FExtractableKey := AExtractable;
  FUsages := AUsages;
end;

procedure TRSAEncryption.Decrypt(AEncryptedData: TJSArrayBuffer;
  AResultType: TCryptoDecryptResultType);
begin
  DecryptP(AEncryptedData, AResultType).catch(@DoErrorCatch);
end;

function TRSAEncryption.DecryptP(AEncryptedData: TJSArrayBuffer;
  AResultType: TCryptoDecryptResultType): TJSPromise;
var
  buffer: TJSUint8Array;
  alg: TJSObject;
begin
  buffer := TJSUint8Array.new(AEncryptedData);
  alg := new(['name', 'RSA-OAEP']);

  case AResultType of
    drtString: Result := window.crypto.subtle.decrypt(alg, FPrivateKey, buffer)._then(@DoDecryptedString);
    drtBinary: Result := window.crypto.subtle.decrypt(alg, FPrivateKey, buffer)._then(@DoDecryptedBinary);
  end;
end;

function TRSAEncryption.DoEncrypted(AResult: JSValue): JSValue;
begin
  if Assigned(OnEncrypted) then
    OnEncrypted(TJSArrayBuffer(AResult));

  Result := TJSArrayBuffer(AResult);
end;

procedure TRSAEncryption.DoImportPrivate(AMode: string; AArrayBuffer: TJSArrayBuffer);
begin
  DoImportPrivateP(AMode, AArrayBuffer).catch(@DoErrorCatch);
end;

function TRSAEncryption.DoImportPrivateP(AMode: string;
  AArrayBuffer: TJSArrayBuffer): TJSPromise;
var
  usg: TStringDynArray;
  o: TJSObject;
  h: string;
begin
  usg := GetUsages(Usages);

  case FHash of
    ehSHA1: h := 'SHA-1';
    ehSHA256: h := 'SHA-256';
    ehSHA384: h := 'SHA-384';
    ehSHA512: h := 'SHA-512';
  end;

  o := new(['name', 'RSA-OAEP', 'hash', h]);
  Result := window.crypto.subtle.importKey(AMode, TJSObject(AArrayBuffer), o, FExtractableKey, usg)._then(@DoPrivateKeyImported);
end;

procedure TRSAEncryption.DoImportPublic(AMode: string;
  AArrayBuffer: TJSArrayBuffer);
begin
  DoImportPublicP(AMode, AArrayBuffer).catch(@DoErrorCatch);
end;

function TRSAEncryption.DoImportPublicP(AMode: string;
  AArrayBuffer: TJSArrayBuffer): TJSPromise;
var
  usg: TStringDynArray;
  o: TJSObject;
  h: string;
begin
  usg := GetUsages(Usages);

  case FHash of
    ehSHA1: h := 'SHA-1';
    ehSHA256: h := 'SHA-256';
    ehSHA384: h := 'SHA-384';
    ehSHA512: h := 'SHA-512';
  end;

  o := new(['name', 'RSA-OAEP', 'hash', h]);
  Result := window.crypto.subtle.importKey(AMode, TJSObject(AArrayBuffer), o, FExtractableKey, usg)._then(@DoPublicKeyImported);
end;

function TRSAEncryption.DoKeyGenerated(AKey: JSValue): JSValue;
var
  ckp: CryptoKeyPair;
begin
  ckp := CryptoKeyPair(AKey);
  FPublicKey := ckp.publicKey;
  FPrivateKey := ckp.privateKey;

  if Assigned(OnKeyCreated) then
    OnKeyCreated(Self);

  Result := True;
end;

function TRSAEncryption.DoPrivateKeyImported(AKey: JSValue): JSValue;
begin
  FPrivateKey := TJSCryptoKey(AKey);

  if Assigned(OnPrivateKeyImported) then
    OnPrivateKeyImported(Self);

  Result := True;
end;

function TRSAEncryption.DoPublicKeyImported(AKey: JSValue): JSValue;
begin
  FPublicKey := TJSCryptoKey(AKey);

  if Assigned(OnPublicKeyImported) then
    OnPublicKeyImported(Self);

  Result := True;
end;

procedure TRSAEncryption.Encrypt(ABinary: TJSUint8Array);
begin
  EncryptP(ABinary).catch(@DoErrorCatch);
end;

function TRSAEncryption.EncryptP(ABinary: TJSUint8Array): TJSPromise;
var
  alg: TJSObject;
begin
  alg := new(['name', 'RSA-OAEP']);
  Result := window.crypto.subtle.encrypt(alg, FPublicKey, ABinary)._then(@DoEncrypted);
end;

function TRSAEncryption.EncryptP(APlainText: string): TJSPromise;
var
  encoded: TJSUint8Array;
  alg: TJSObject;
begin
  if not IsTextEncoderSupported then
    Exit;

  encoded := GetMessageEncoding(APlainText);
  alg := new(['name', 'RSA-OAEP']);
  Result := window.crypto.subtle.encrypt(alg, FPublicKey, encoded)._then(@DoEncrypted);
end;

procedure TRSAEncryption.ExportKey(AKeyType: TCryptoAsymKeyType;
  AFormat: TCryptoExportImportFormat);
begin
  ExportKeyP(AKeyType, AFormat).catch(@DoErrorCatch);
end;

function TRSAEncryption.ExportKeyP(AKeyType: TCryptoAsymKeyType;
  AFormat: TCryptoExportImportFormat): TJSPromise;
begin
  case AKeyType of
    aktPrivate:
    begin
      case AFormat of
        efPKCS8: Result := window.crypto.subtle.exportKey('pkcs8', FPrivateKey)._then(@DoKeyExportedPKCS);
        efJSON: Result := window.crypto.subtle.exportKey('jwk', FPrivateKey)._then(@DoKeyExportedJSON);
      end;
    end;
    aktPublic:
    begin
      case AFormat of
        efSPKI: Result := window.crypto.subtle.exportKey('spki', FPublicKey)._then(@DoKeyExportedSPKI);
        efJSON: Result := window.crypto.subtle.exportKey('jwk', FPublicKey)._then(@DoKeyExportedJSON);
      end;
    end;
  end;
end;

procedure TRSAEncryption.Encrypt(APlainText: string);
begin
  EncryptP(APlainText).catch(@DoErrorCatch);
end;

constructor TRSAEncryption.Create(AModulusLength: TRSAModulusLength;
  AHash: TCryptoHash; AExtractable: Boolean);
begin
  FModulusLength := AModulusLength;
  FHash := AHash;
  FExtractableKey := AExtractable;
  FUsages := [kuEncrypt, kuDecrypt];
end;

procedure TRSAEncryption.GenerateKey;
begin
  GenerateKeyP._then(@DoKeyGenerated).catch(@DoErrorCatch);
end;

function TRSAEncryption.GenerateKeyP: TJSPromise;
var
  usg: TStringDynArray;
  pe: TJSUint8Array;
  alg: TJSObject;
  len: Integer;
  h: string;
begin
  usg := GetUsages(Usages);

  case FModulusLength of
    rml2048: len := 2048;
    rml3072: len := 3072;
    rml4096: len := 4096;
  end;

  case FHash of
    ehSHA1: h := 'SHA-1';
    ehSHA256: h := 'SHA-256';
    ehSHA384: h := 'SHA-384';
    ehSHA512: h := 'SHA-512';
  end;

  pe := TJSUint8Array.new(3);
  pe[0] := 1;
  pe[1] := 0;
  pe[2] := 1;

  alg := new(['name', 'RSA-OAEP', 'modulusLength', len, 'publicExponent', pe, 'hash', h]);

  Result := window.crypto.subtle.generateKey(alg, FExtractableKey, usg)._then(@DoKeyGenerated);
end;

procedure TRSAEncryption.ImportKey(ABinary: TJSUint8Array;
  AKeyType: TCryptoAsymKeyType);
begin
  case AKeyType of
    aktPrivate: DoImportPrivate('pkcs8', ABinary.buffer);
    aktPublic: DoImportPublic('spki', ABinary.buffer);
  end;
end;

function TRSAEncryption.ImportKeyP(AKey: string; AKeyType: TCryptoAsymKeyType;
  AFormat: TCryptoExportImportFormat): TJSPromise;
var
  ab: TJSArrayBuffer;
begin
  case AKeyType of
    aktPrivate:
    begin
      case AFormat of
        efPKCS8:
        begin
          ab := GetPKCSAndSPKIImportAB(AKey);
          Result := DoImportPrivateP('pkcs8', ab);
        end;
        efJSON:
        begin
          ab := TJSArrayBuffer(TJSJSON.parse(AKey));
          Result := DoImportPrivateP('jwk', ab);
        end;
      end;
    end;
    aktPublic:
    begin
      case AFormat of
        efSPKI:
        begin
          ab := GetPKCSAndSPKIImportAB(AKey);
          Result := DoImportPublicP('spki', ab);
        end;
        efJSON:
        begin
          ab := TJSArrayBuffer(TJSJSON.parse(AKey));
          Result := DoImportPublicP('jwk', ab);
        end;
      end;
    end;
  end;
end;

function TRSAEncryption.ImportKeyP(ABinary: TJSUint8Array;
  AKeyType: TCryptoAsymKeyType): TJSPromise;
begin
  case AKeyType of
    aktPrivate: Result := DoImportPrivateP('pkcs8', ABinary.buffer);
    aktPublic: Result := DoImportPublicP('spki', ABinary.buffer);
  end;
end;

procedure TRSAEncryption.ImportKey(AKey: string; AKeyType: TCryptoAsymKeyType;
  AFormat: TCryptoExportImportFormat);
var
  ab: TJSArrayBuffer;
begin
  case AKeyType of
    aktPrivate:
    begin
      case AFormat of
        efPKCS8:
        begin
          ab := GetPKCSAndSPKIImportAB(AKey);
          DoImportPrivate('pkcs8', ab);
        end;
        efJSON:
        begin
          ab := TJSArrayBuffer(TJSJSON.parse(AKey));
          DoImportPrivate('jwk', ab);
        end;
      end;
    end;
    aktPublic:
    begin
      case AFormat of
        efSPKI:
        begin
          ab := GetPKCSAndSPKIImportAB(AKey);
          DoImportPublic('spki', ab);
        end;
        efJSON:
        begin
          ab := TJSArrayBuffer(TJSJSON.parse(AKey));
          DoImportPublic('jwk', ab);
        end;
      end;
    end;
  end;
end;

procedure TRSAEncryption.SetHash(const Value: TCryptoHash);
begin
  FHash := Value;
end;

procedure TRSAEncryption.SetModulusLength(const Value: TRSAModulusLength);
begin
  FModulusLength := Value;
end;

procedure TRSAEncryption.SetPrivateKey(const Value: TJSCryptoKey);
begin
  FPrivateKey := Value;
end;

procedure TRSAEncryption.SetPublicKey(const Value: TJSCryptoKey);
begin
  FPublicKey := Value;
end;

procedure TRSAEncryption.UnwrapKey(AImportFormat: TCryptoExportImportFormat;
  AKey: TJSArrayBuffer; AKeyAlgorithm: JSValue; AExtractable: Boolean;
  AKeyUsages: TCryptoKeyUsages);
begin
  UnwrapKeyP(AImportFormat, AKey, AKeyAlgorithm, AExtractable, AKeyUsages).catch(@DoErrorCatch);
end;

function TRSAEncryption.UnwrapKeyP(AImportFormat: TCryptoExportImportFormat;
  AKey: TJSArrayBuffer; AKeyAlgorithm: JSValue; AExtractable: Boolean;
  AKeyUsages: TCryptoKeyUsages): TJSPromise;
var
  f: string;
  alg: TJSObject;
  usg: TStringDynArray;
  buff: TJSUint8Array;
begin
  alg := new(['name', 'RSA-OAEP']);
  usg := GetUsages(AKeyUsages);
  buff := TJSUint8Array.new(AKey);

  case AImportFormat of
    efRaw: f := 'raw';
    efPKCS8: f := 'pkcs8';
    efSPKI: f := 'spki';
    efJSON: f := 'jwk';
  end;

  Result := window.crypto.subtle.unwrapkey(f, buff, FPrivateKey, alg, AKeyAlgorithm, AExtractable, usg)._then(@DoKeyUnwrapped);
end;

procedure TRSAEncryption.WrapKey(AKey: TJSCryptoKey;
  AExportFormat: TCryptoExportImportFormat);
begin
  WrapKeyP(AKey, AExportFormat).catch(@DoErrorCatch);
end;

function TRSAEncryption.WrapKeyP(AKey: TJSCryptoKey;
  AExportFormat: TCryptoExportImportFormat): TJSPromise;
var
  wa: TJSObject;
  f: string;
begin
  wa := new(['name', 'RSA-OAEP']);

  case AExportFormat of
    efRaw: f := 'raw';
    efPKCS8: f := 'pkcs8';
    efSPKI: f := 'spki';
    efJSON: f := 'jwk';
  end;

  Result := window.crypto.subtle.wrapKey(f, AKey, FPublicKey, wa)._then(@DoKeyWrapped);
end;

{ TCryptoBase }

function TCryptoBase.DoErrorCatch(Err: JSValue): JSValue;  //TODO REMOVE
var
  s: string;
begin
  asm
    s = Err.name + ': ' + Err.message;
  end;

  if Assigned(OnError) then
    OnError(s);

  Result := s;
end;

function TCryptoBase.DoKeyExportedJSON(AResult: JSValue): JSValue;
var
  s: string;
begin
  s := TJSJSON.stringify(AResult);

  if Assigned(OnKeyExportedJSON) then
    OnKeyExportedJSON(s);

  Result := s;
end;

function TCryptoBase.DoKeyExportedPKCS(AResult: JSValue): JSValue;
var
  s: string;
begin
  s := GetPKCSAndSPKIExportString(AResult);

  if Assigned(OnKeyExportedPKCS8) then
    OnKeyExportedPKCS8(s, TJSUint8Array.new(TJSArrayBuffer(AResult)));

  Result := s;
end;

function TCryptoBase.DoKeyExportedRaw(AResult: JSValue): JSValue;
var
  arr: TJSArrayBuffer;
  bin: TJSUint8Array;
begin
  arr := TJSArrayBuffer(AResult);
  bin := TJSUint8Array.new(arr);

  if Assigned(OnKeyExportedRaw) then
    OnKeyExportedRaw(bin);

  Result := bin;
end;

function TCryptoBase.DoKeyExportedSPKI(AResult: JSValue): JSValue;
var
  s: string;
begin
  s := GetPKCSAndSPKIExportString(AResult);

  if Assigned(OnKeyExportedSPKI) then
    OnKeyExportedSPKI(s, TJSUint8Array.new(TJSArrayBuffer(AResult)));

  Result := s;
end;

function TCryptoBase.GetPKCSAndSPKIExportString(AResult: JSValue): string;
var
  header, footer, s: string;
  ab: TJSArrayBuffer;
begin
  s := '';
  header := '-----BEGIN PRIVATE KEY-----';
  footer := '-----END PRIVATE KEY-----';
  ab := TJSArrayBuffer(AResult);

  s := ABToBase(ab);
  s := header + sLineBreak + s + sLineBreak + footer;
  Result := s;
end;

function TCryptoBase.GetPKCSAndSPKIImportAB(APEMString: string): TJSArrayBuffer;
var
  header, footer, base: string;
  f, cc: Integer;
begin
  header := '-----BEGIN PRIVATE KEY-----';
  footer := '-----END PRIVATE KEY-----';
  f := Length(header) + 1;
  cc := Length(APEMString) - Length(header) - Length(footer);
  base := Copy(APEMString, f, cc);
  Result := BaseToAB(base);
end;

function TCryptoBase.GetUsages(AUsages: TCryptoKeyUsages): TStringDynArray;
var
  I: Integer;
  key: TCryptoKeyUsage;
  strs: TStringList;
begin
  Result := [];
  strs := TStringList.Create;
  try
    for key := Low(TCryptoKeyUsages) to High(TCryptoKeyUsages) do
      if key in AUsages then
        strs.Add(TCryptoKeyUsageValue[key]);

    SetLength(Result, strs.Count);

    for I := 0 to strs.Count - 1 do
      Result[I] := strs[I];
  finally
    strs.Free;
  end;
end;

procedure TCryptoBase.SetExtractableKey(const Value: Boolean);
begin
  FExtractableKey := Value;
end;

{ TSignatureBase }

procedure TSignatureBase.Sign(AText: string);
begin
  //
end;

function TSignatureBase.DoSignatureCreated(AResult: JSValue): JSValue;
begin
  if Assigned(OnSigned) then
    OnSigned(TJSArrayBuffer(AResult));

  Result := TJSArrayBuffer(AResult);
end;

function TSignatureBase.DoSignatureVerify(AResult: JSValue): JSValue;
begin
  if Assigned(OnVerify) then
    OnVerify(Boolean(AResult));

  Result := Boolean(AResult);
end;

procedure TSignatureBase.Sign(ABinary: TJSUint8Array);
begin
  //
end;

function TSignatureBase.SignP(AText: string): TJSPromise;
begin
  Result := nil;
end;

function TSignatureBase.SignP(ABinary: TJSUint8Array): TJSPromise;
begin
  Result := nil;
end;

procedure TSignatureBase.Verify(ASignature, AData: TJSArrayBuffer);
begin
  //
end;

function TSignatureBase.VerifyP(ASignature, AData: TJSArrayBuffer): TJSPromise;
begin
  Result := nil;
end;

{ TRSASignature }

procedure TRSASignature.AfterConstruction;
begin
  inherited;
  GenerateKey;
end;

constructor TRSASignature.Create(AModulusLength: TRSAModulusLength;
  AHash: TCryptoHash);
begin
  FModulusLength := AModulusLength;
  FHash := AHash;
  FExtractableKey := False;
  FUsages := [kuSign, kuVerify];
end;

constructor TRSASignature.Create(AModulusLength: TRSAModulusLength);
begin
  FModulusLength := AModulusLength;
  FHash := ehSHA256;
  FExtractableKey := False;
  FUsages := [kuSign, kuVerify];
end;

constructor TRSASignature.Create;
begin
  FModulusLength := rml4096;
  FHash := ehSHA256;
  FExtractableKey := False;
  FUsages := [kuSign, kuVerify];
end;

constructor TRSASignature.Create(AModulusLength: TRSAModulusLength;
  AHash: TCryptoHash; AExtractable: Boolean);
begin
  FModulusLength := AModulusLength;
  FHash := AHash;
  FExtractableKey := AExtractable;
  FUsages := [kuSign, kuVerify];
end;

constructor TRSASignature.Create(AModulusLength: TRSAModulusLength;
  AHash: TCryptoHash; AExtractable: Boolean; AUsages: TCryptoKeyUsages);
begin
  FModulusLength := AModulusLength;
  FHash := AHash;
  FExtractableKey := AExtractable;
  FUsages := AUsages;
end;

procedure TRSASignature.DoImportPrivate(AMode: string;
  AArrayBuffer: TJSArrayBuffer);
begin
  DoImportPrivateP(AMode, AArrayBuffer).catch(@DoErrorCatch);
end;

function TRSASignature.DoImportPrivateP(AMode: string;
  AArrayBuffer: TJSArrayBuffer): TJSPromise;
var
  usg: TStringDynArray;
  h: string;
  o: TJSObject;
begin
  usg := GetUsages(Usages);

  case FHash of
    ehSHA1: h := 'SHA-1';
    ehSHA256: h := 'SHA-256';
    ehSHA384: h := 'SHA-384';
    ehSHA512: h := 'SHA-512';
  end;

  o := new(['name', 'RSASSA-PKCS1-v1_5', 'hash', h]);
  Result := window.crypto.subtle.importKey(AMode, TJSObject(AArrayBuffer), o, FExtractableKey, usg)._then(@DoPrivateKeyImported);
end;

procedure TRSASignature.DoImportPublic(AMode: string;
  AArrayBuffer: TJSArrayBuffer);
begin
  DoImportPublicP(AMode, AArrayBuffer).catch(@DoErrorCatch);
end;

function TRSASignature.DoImportPublicP(AMode: string;
  AArrayBuffer: TJSArrayBuffer): TJSPromise;
var
  usg: TStringDynArray;
  o: TJSObject;
  h: string;
begin
  usg := GetUsages(Usages);

  case FHash of
    ehSHA1: h := 'SHA-1';
    ehSHA256: h := 'SHA-256';
    ehSHA384: h := 'SHA-384';
    ehSHA512: h := 'SHA-512';
  end;

  o := new(['name', 'RSASSA-PKCS1-v1_5', 'hash', h]);
  Result := window.crypto.subtle.importKey(AMode, TJSObject(AArrayBuffer), o, FExtractableKey, usg)._then(@DoPublicKeyImported);
end;

function TRSASignature.DoKeyGenerated(AKey: JSValue): JSValue;
var
  ckp: CryptoKeyPair;
begin
  ckp := CryptoKeyPair(AKey);
  FPublicKey := ckp.publicKey;
  FPrivateKey := ckp.privateKey;

  if Assigned(OnKeyCreated) then
    OnKeyCreated(Self);

  Result := True;
end;

function TRSASignature.DoPrivateKeyImported(AKey: JSValue): JSValue;
begin
  FPrivateKey := TJSCryptoKey(AKey);

  if Assigned(OnPrivateKeyImported) then
    OnPrivateKeyImported(Self);

  Result := True;
end;

function TRSASignature.DoPublicKeyImported(AKey: JSValue): JSValue;
begin
  FPublicKey := TJSCryptoKey(AKey);

  if Assigned(OnPublicKeyImported) then
    OnPublicKeyImported(Self);

  Result := True;
end;

procedure TRSASignature.ExportKey(AKeyType: TCryptoAsymKeyType;
  AFormat: TCryptoExportImportFormat);
begin
  ExportKeyP(AKeyType, AFormat).catch(@DoErrorCatch);
end;

function TRSASignature.ExportKeyP(AKeyType: TCryptoAsymKeyType;
  AFormat: TCryptoExportImportFormat): TJSPromise;
begin
  case AKeyType of
    aktPrivate:
    begin
      case AFormat of
        efPKCS8: Result := window.crypto.subtle.exportKey('pkcs8', FPrivateKey)._then(@DoKeyExportedPKCS);
        efJSON: Result := window.crypto.subtle.exportKey('jwk', FPrivateKey)._then(@DoKeyExportedJSON);
      end;
    end;
    aktPublic:
    begin
      case AFormat of
        efSPKI: Result := window.crypto.subtle.exportKey('spki', FPublicKey)._then(@DoKeyExportedSPKI);
        efJSON: Result := window.crypto.subtle.exportKey('jwk', FPublicKey)._then(@DoKeyExportedJSON);
      end;
    end;
  end;
end;

procedure TRSASignature.GenerateKey;
begin
  GenerateKeyP.catch(@DoErrorCatch);
end;

function TRSASignature.GenerateKeyP: TJSPromise;
var
  usg: TStringDynArray;
  pe: TJSUint8Array;
  alg: TJSObject;
  len: Integer;
  h: string;
begin
  usg := GetUsages(Usages);

  case FModulusLength of
    rml2048: len := 2048;
    rml3072: len := 3072;
    rml4096: len := 4096;
  end;

  case FHash of
    ehSHA1: h := 'SHA-1';
    ehSHA256: h := 'SHA-256';
    ehSHA384: h := 'SHA-384';
    ehSHA512: h := 'SHA-512';
  end;

  pe := TJSUint8Array.new(3);
  pe[0] := 1;
  pe[1] := 0;
  pe[2] := 1;

  //example for RSA-PSS: alg := new(['name', 'RSA-PSS', 'modulusLength', len, 'publicExponent', pe, 'hash', h]);
  alg := new(['name', 'RSASSA-PKCS1-v1_5', 'modulusLength', len, 'publicExponent', pe, 'hash', h]);

  Result := window.crypto.subtle.generateKey(alg, FExtractableKey, usg)._then(@DoKeyGenerated);
end;

procedure TRSASignature.ImportKey(ABinary: TJSUint8Array;
  AKeyType: TCryptoAsymKeyType);
begin
  case AKeyType of
    aktPrivate: DoImportPrivate('pkcs8', ABinary.buffer);
    aktPublic: DoImportPublic('spki', ABinary.buffer);
  end;
end;

function TRSASignature.ImportKeyP(AKey: string; AKeyType: TCryptoAsymKeyType;
  AFormat: TCryptoExportImportFormat): TJSPromise;
var
  ab: TJSArrayBuffer;
begin
  case AKeyType of
    aktPrivate:
    begin
      case AFormat of
        efPKCS8:
        begin
          ab := GetPKCSAndSPKIImportAB(AKey);
          Result := DoImportPrivateP('pkcs8', ab);
        end;
        efJSON:
        begin
          ab := TJSArrayBuffer(TJSJSON.parse(AKey));
          Result := DoImportPrivateP('jwk', ab);
        end;
      end;
    end;
    aktPublic:
    begin
      case AFormat of
        efSPKI:
        begin
          ab := GetPKCSAndSPKIImportAB(AKey);
          Result := DoImportPublicP('spki', ab);
        end;
        efJSON:
        begin
          ab := TJSArrayBuffer(TJSJSON.parse(AKey));
          Result := DoImportPublicP('jwk', ab);
        end;
      end;
    end;
  end;
end;

function TRSASignature.ImportKeyP(ABinary: TJSUint8Array;
  AKeyType: TCryptoAsymKeyType): TJSPromise;
begin
  case AKeyType of
    aktPrivate: Result := DoImportPrivateP('pkcs8', ABinary.buffer);
    aktPublic: Result := DoImportPublicP('spki', ABinary.buffer);
  end;
end;

procedure TRSASignature.ImportKey(AKey: string; AKeyType: TCryptoAsymKeyType;
  AFormat: TCryptoExportImportFormat);
var
  ab: TJSArrayBuffer;
begin
  case AKeyType of
    aktPrivate:
    begin
      case AFormat of
        efPKCS8:
        begin
          ab := GetPKCSAndSPKIImportAB(AKey);
          DoImportPrivate('pkcs8', ab);
        end;
        efJSON:
        begin
          ab := TJSArrayBuffer(TJSJSON.parse(AKey));
          DoImportPrivate('jwk', ab);
        end;
      end;
    end;
    aktPublic:
    begin
      case AFormat of
        efSPKI:
        begin
          ab := GetPKCSAndSPKIImportAB(AKey);
          DoImportPublic('spki', ab);
        end;
        efJSON:
        begin
          ab := TJSArrayBuffer(TJSJSON.parse(AKey));
          DoImportPublic('jwk', ab);
        end;
      end;
    end;
  end;
end;

procedure TRSASignature.SetPrivateKey(const Value: TJSCryptoKey);
begin
  FPrivateKey := Value;
end;

procedure TRSASignature.SetPublicKey(const Value: TJSCryptoKey);
begin
  FPublicKey := Value;
end;

procedure TRSASignature.Sign(AText: string);
begin
  SignP(AText).catch(@DoErrorCatch);
end;

procedure TRSASignature.Sign(ABinary: TJSUint8Array);
begin
  SignP(ABinary).catch(@DoErrorCatch);
end;

function TRSASignature.SignP(AText: string): TJSPromise;
var
  encoded: TJSUint8Array;
begin
  encoded := GetMessageEncoding(AText);
  Result := window.crypto.subtle.sign('RSASSA-PKCS1-v1_5', FPrivateKey, encoded)._then(@DoSignatureCreated);
end;

function TRSASignature.SignP(ABinary: TJSUint8Array): TJSPromise;
begin
  Result := window.crypto.subtle.sign('RSASSA-PKCS1-v1_5', FPrivateKey, ABinary)._then(@DoSignatureCreated);
end;

procedure TRSASignature.Verify(ASignature, AData: TJSArrayBuffer);
begin
  VerifyP(ASignature, AData).catch(@DoErrorCatch);
end;

function TRSASignature.VerifyP(ASignature, AData: TJSArrayBuffer): TJSPromise;
begin
  Result := window.crypto.subtle.verify('RSASSA-PKCS1-v1_5', FPublicKey, TJSBufferSource(ASignature), TJSBufferSource(AData))._then(@DoSignatureVerify);
end;

{ THMACSignature }

constructor THMACSignature.Create;
begin
  FHash := ehSHA256;
  FExtractableKey := False;
  FUsages := [kuSign, kuVerify];
end;

constructor THMACSignature.Create(AHash: TCryptoHash);
begin
  FHash := AHash;
  FExtractableKey := False;
  FUsages := [kuSign, kuVerify];
end;

constructor THMACSignature.Create(AHash: TCryptoHash;
  AExtractable: Boolean);
begin
  FHash := AHash;
  FExtractableKey := AExtractable;
  FUsages := [kuSign, kuVerify];
end;

procedure THMACSignature.AfterConstruction;
begin
  inherited;
  GenerateKey;
end;

constructor THMACSignature.Create(AHash: TCryptoHash; AExtractable: Boolean;
  AUsages: TCryptoKeyUsages);
begin
  FHash := AHash;
  FExtractableKey := AExtractable;
  FUsages := AUsages;
end;

procedure THMACSignature.DoImport(AMode: string; AArrayBuffer: TJSArrayBuffer);
begin
  DoImportP(AMode, AArrayBuffer).catch(@DoErrorCatch);
end;

function THMACSignature.DoImportP(AMode: string;
  AArrayBuffer: TJSArrayBuffer): TJSPromise;
var
  usg: TStringDynArray;
  h: string;
  o: TJSObject;
begin
  usg := GetUsages(Usages);

  case FHash of
    ehSHA1: h := 'SHA-1';
    ehSHA256: h := 'SHA-256';
    ehSHA384: h := 'SHA-384';
    ehSHA512: h := 'SHA-512';
  end;

  o := new(['name', 'HMAC', 'hash', h]);
  Result := window.crypto.subtle.importKey(AMode, TJSObject(AArrayBuffer), o, FExtractableKey, usg)._then(@DoKeyImported);
end;

function THMACSignature.DoKeyGenerated(AKey: JSValue): JSValue;
begin
  if not Assigned(FCryptoKey) then
  begin
    FCryptoKey := TJSCryptoKey(AKey);

    if Assigned(OnKeyCreated) then
      OnKeyCreated(Self);
  end;

  Result := True;
end;

function THMACSignature.DoKeyImported(AKey: JSValue): JSValue;
begin
  FCryptoKey := TJSCryptoKey(AKey);

  if Assigned(OnKeyImported) then
    OnKeyImported(Self);

  Result := True;
end;

procedure THMACSignature.ExportKey(AFormat: TCryptoExportImportFormat);
begin
  ExportKeyP(AFormat).catch(@DoErrorCatch);
end;

function THMACSignature.ExportKeyP(
  AFormat: TCryptoExportImportFormat): TJSPromise;
begin
  case AFormat of
    efRaw: Result := window.crypto.subtle.exportKey('raw', FCryptoKey)._then(@DoKeyExportedRaw);
    efJSON: Result := window.crypto.subtle.exportKey('jwk', FCryptoKey)._then(@DoKeyExportedJSON);
  end;
end;

procedure THMACSignature.GenerateKey;
begin
  GenerateKeyP.catch(@DoErrorCatch);
end;

function THMACSignature.GenerateKeyP: TJSPromise;
var
  usg: TStringDynArray;
  alg, ho: TJSObject;
  h: string;
begin
  usg := GetUsages(Usages);

  case FHash of
    ehSHA1: h := 'SHA-1';
    ehSHA256: h := 'SHA-256';
    ehSHA384: h := 'SHA-384';
    ehSHA512: h := 'SHA-512';
  end;

  ho := new(['name', h]);
  alg := new(['name', 'HMAC', 'hash', ho]);

  Result := window.crypto.subtle.generateKey(alg, FExtractableKey, usg)._then(@DoKeyGenerated);
end;

procedure THMACSignature.ImportKey(AJSON: string);
var
  ab: TJSArrayBuffer;
begin
  ab := TJSArrayBuffer(TJSJSON.parse(AJSON));
  DoImport('jwk', ab);
end;

procedure THMACSignature.ImportKey(ABinary: TJSUint8Array);
begin
  DoImport('raw', ABinary.buffer);
end;

procedure THMACSignature.ImportKey(ARaw: TJSArrayBuffer);
begin
  DoImport('raw', ARaw);
end;

function THMACSignature.ImportKeyP(AJSON: string): TJSPromise;
var
  ab: TJSArrayBuffer;
begin
  ab := TJSArrayBuffer(TJSJSON.parse(AJSON));
  Result := DoImportP('jwk', ab);
end;

function THMACSignature.ImportKeyP(ABinary: TJSUint8Array): TJSPromise;
begin
  Result := DoImportP('raw', ABinary.buffer);
end;

function THMACSignature.ImportKeyP(ARaw: TJSArrayBuffer): TJSPromise;
begin
  Result := DoImportP('raw', ARaw);
end;

procedure THMACSignature.SetCryptoKey(const Value: TJSCryptoKey);
begin
  FCryptoKey := Value;
end;

procedure THMACSignature.Sign(ABinary: TJSUint8Array);
begin
  SignP(ABinary).catch(@DoErrorCatch);
end;

function THMACSignature.SignP(AText: string): TJSPromise;
var
  encoded: TJSUint8Array;
begin
  encoded := GetMessageEncoding(AText);
  Result := window.crypto.subtle.sign('HMAC', FCryptoKey, encoded)._then(@DoSignatureCreated);
end;

function THMACSignature.SignP(ABinary: TJSUint8Array): TJSPromise;
begin
  Result := window.crypto.subtle.sign('HMAC', FCryptoKey, ABinary)._then(@DoSignatureCreated);
end;

procedure THMACSignature.Verify(ASignature, AData: TJSArrayBuffer);
begin
  VerifyP(ASignature, AData).catch(@DoErrorCatch);
end;

function THMACSignature.VerifyP(ASignature, AData: TJSArrayBuffer): TJSPromise;
begin
  Result := window.crypto.subtle.verify('HMAC', FCryptoKey, TJSBufferSource(ASignature), TJSBufferSource(AData))._then(@DoSignatureVerify);
end;

procedure THMACSignature.Sign(AText: string);
begin
  SignP(AText).catch(@DoErrorCatch);
end;

{ TSHAHash }

constructor TSHAHash.Create;
begin
  FHashType := ehSHA256;
end;

constructor TSHAHash.Create(AHashType: TCryptoHash);
begin
  FHashType := AHashType;
end;

function TSHAHash.Hash(AText: string): TJSPromise;
begin
  Result := TJSPromise.New(
  procedure (AResolve, AReject: TJSPromiseResolver)
  var
    arr: TJSUint8Array;
    s: string;
  begin
    arr := GetMessageEncoding(AText);
    case FHashType of
      ehSHA1: s := 'SHA-1';
      ehSHA384: s := 'SHA-384';
      ehSHA512: s := 'SHA-512';
    else
      s := 'SHA-256';
    end;
    window.crypto.subtle.digest(s, arr)._then(function (aHash: JSValue): JSValue
    begin
      AResolve(ABToHex(TJSArrayBuffer(aHash)));
    end);
  end);
end;

end.
